import { WebMercatorViewport } from "@math.gl/web-mercator";
import { useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { FeatureCollection } from "geojson";
import {
  getFeatureCollectionBbox,
  isValidLngLatBbox,
} from "common/utils/mapGeometry";
import { transformMapTileRequest } from "common/utils/mapTransformRequest";
import {
  ArableFieldGeoJSONLayers,
  FieldGeoJSONLayers,
} from "features/mapLayers";
import { ONLINE_MAP_STYLE } from "features/mapLayers/offlineMapStyle";
import { useEffect, useRef, useState } from "react";
import { Map, MapRef } from "react-map-gl/mapbox";

interface FieldMapPreviewProps {
  geojson: FeatureCollection;
  isArable?: boolean;
  fieldIds?: string[];
}

interface ViewportState {
  latitude: number;
  longitude: number;
  zoom: number;
  bearing: number;
  pitch: number;
  width: number;
  height: number;
}

const initialViewport: ViewportState = {
  latitude: 52.794201,
  longitude: -1.1945036,
  zoom: 12,
  bearing: 0,
  pitch: 0,
  width: 280,
  height: 120,
};

const MapContainer = styled("div")({
  width: "100%",
  height: "100%",
  position: "relative",
  "& .maplibregl-ctrl-logo": {
    display: "none !important",
  },
  "& .maplibregl-ctrl-attrib": {
    display: "none !important",
  },
});

export default function FieldMapPreview({
  geojson,
  isArable,
  fieldIds,
}: FieldMapPreviewProps) {
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<ViewportState>(initialViewport);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getViewportForBounds = (
    geojson: FeatureCollection,
    width: number,
    height: number
  ): ViewportState => {
    if (!width || !height || geojson.features.length === 0) {
      return {
        ...initialViewport,
        width,
        height,
      };
    }

    // Validate viewport dimensions
    if (width <= 0 || height <= 0 || !isFinite(width) || !isFinite(height)) {
      return {
        ...initialViewport,
        width,
        height,
      };
    }

    const bounds = getFeatureCollectionBbox(geojson);
    if (!bounds || !isValidLngLatBbox(bounds)) {
      return {
        ...initialViewport,
        width,
        height,
      };
    }

    const [minLng, minLat, maxLng, maxLat] = bounds;

    // Validate padding doesn't exceed viewport dimensions
    const padding = 20;
    if (padding * 2 >= width || padding * 2 >= height) {
      // Padding too large - return initial viewport with current dimensions
      return {
        ...initialViewport,
        width,
        height,
      };
    }

    try {
      const viewport = new WebMercatorViewport({
        ...initialViewport,
        width,
        height,
      });

      const bounds: [[number, number], [number, number]] = [
        [minLng, minLat],
        [maxLng, maxLat],
      ];
      const { longitude, latitude, zoom } = viewport.fitBounds(bounds, {
        padding: 20,
      });

      // Validate the returned values
      if (!isFinite(longitude) || !isFinite(latitude) || !isFinite(zoom)) {
        return {
          ...initialViewport,
          width,
          height,
        };
      }

      return {
        ...initialViewport,
        longitude,
        latitude,
        zoom: zoom - 0.5, // Zoom out slightly to give some padding
        width,
        height,
      };
    } catch (error) {
      // If fitBounds throws an error (e.g., assertion failure), return initial viewport
      console.warn("Error fitting bounds to viewport:", error);
      return {
        ...initialViewport,
        width,
        height,
      };
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      const newViewport = getViewportForBounds(geojson, width, height);
      setViewport(newViewport);
    }
  }, [geojson]);

  const handleTransformRequest = (url: string) => transformMapTileRequest(url);

  const noop = () => undefined;

  return (
    <MapContainer ref={containerRef}>
      <Map
        ref={mapRef}
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
        longitude={viewport.longitude}
        latitude={viewport.latitude}
        zoom={viewport.zoom}
        bearing={viewport.bearing}
        pitch={viewport.pitch}
        mapStyle={ONLINE_MAP_STYLE}
        dragPan={false}
        dragRotate={false}
        scrollZoom={false}
        doubleClickZoom={false}
        touchZoomRotate={false}
        keyboard={false}
        maxPitch={0}
        pitchWithRotate={false}
        transformRequest={handleTransformRequest}
        attributionControl={false}
      >
        <FieldGeoJSONLayers
          fieldIds={fieldIds}
          fillLayers={true}
          setTooltipOverMarker={noop}
          mapRef={mapRef}
        />
        <ArableFieldGeoJSONLayers
          fieldIds={fieldIds}
          fillLayers={true}
          setTooltipOverMarker={noop}
          mapRef={mapRef}
        />
      </Map>
    </MapContainer>
  );
}
